import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertCircle, Search, ChevronUp, ChevronDown, X } from 'lucide-react';
import { fetchCookbookById, getCookbookPdfUrl } from '@/lib/cookbooks';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import PdfViewer, { PdfViewerHandle } from '@/components/PdfViewer';
import styles from './CookbookViewerPage.module.css';

export default function CookbookViewerPage() {
  const { id } = useParams<{ id: string }>();
  const { hasFullAccess } = useAuth();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const pdfViewerRef = useRef<PdfViewerHandle>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: cookbook, isLoading } = useQuery({
    queryKey: ['cookbook', id],
    queryFn: () => fetchCookbookById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!cookbook) return;

    const loadPdf = async () => {
      try {
        const { url, isPreview: preview } = await getCookbookPdfUrl(cookbook, hasFullAccess);
        setPdfUrl(url);
        setIsPreview(preview);
        setLoadError(null);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Failed to load cookbook');
      }
    };

    loadPdf();
  }, [cookbook, hasFullAccess]);

  const handleSearchResults = (total: number, current: number) => {
    setMatchCount(total);
    setCurrentMatch(current);
  };

  const toggleSearch = () => {
    if (isSearchOpen) {
      setSearchQuery('');
      setMatchCount(0);
      setCurrentMatch(0);
      pdfViewerRef.current?.clearSearch();
    } else {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    setIsSearchOpen(!isSearchOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        pdfViewerRef.current?.prevMatch();
      } else {
        pdfViewerRef.current?.nextMatch();
      }
    } else if (e.key === 'Escape') {
      toggleSearch();
    }
  };

  if (isLoading) return <LoadingSpinner />;

  if (!cookbook) {
    return (
      <div className={styles.errorState}>
        <AlertCircle size={48} />
        <h2>Cookbook Not Found</h2>
        <Link to="/cookbooks" className={styles.backLink}>Return to Library</Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Link to="/cookbooks" className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to Cookbooks
        </Link>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>{cookbook.title}</h1>
          {isPreview && <span className={styles.previewBadge}>Preview</span>}
        </div>
        <div className={styles.searchSection}>
          {isSearchOpen ? (
            <div className={styles.searchBar}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className={styles.searchInput}
              />
              {matchCount > 0 && (
                <span className={styles.matchCount}>
                  {currentMatch} of {matchCount}
                </span>
              )}
              {searchQuery && matchCount === 0 && (
                <span className={styles.noMatches}>No matches</span>
              )}
              <button 
                className={styles.navButton} 
                onClick={() => pdfViewerRef.current?.prevMatch()} 
                disabled={matchCount === 0}
                aria-label="Previous match"
              >
                <ChevronUp size={18} />
              </button>
              <button 
                className={styles.navButton} 
                onClick={() => pdfViewerRef.current?.nextMatch()} 
                disabled={matchCount === 0}
                aria-label="Next match"
              >
                <ChevronDown size={18} />
              </button>
              <button className={styles.closeSearch} onClick={toggleSearch} aria-label="Close search">
                <X size={18} />
              </button>
            </div>
          ) : (
            <button className={styles.searchToggle} onClick={toggleSearch} aria-label="Open search">
              <Search size={20} />
            </button>
          )}
        </div>
      </header>

      {loadError ? (
        <div className={styles.errorState}>
          <AlertCircle size={48} />
          <h2>{loadError}</h2>
          {loadError.includes('Subscription') && (
            <p className={styles.upgradeText}>Upgrade to Premium to access all cookbooks</p>
          )}
          <Link to="/cookbooks" className={styles.backLink}>Return to Library</Link>
        </div>
      ) : pdfUrl ? (
        <div className={styles.viewerWrapper}>
          <PdfViewer 
            ref={pdfViewerRef}
            url={pdfUrl} 
            searchQuery={searchQuery}
            onSearchResults={handleSearchResults}
          />
        </div>
      ) : (
        <LoadingSpinner />
      )}

      {isPreview && !hasFullAccess && (
        <div className={styles.previewBanner}>
          <p>You're viewing a preview. Upgrade to Premium to access the full cookbook.</p>
        </div>
      )}
    </div>
  );
}
